import {
  Flex,
  Box,
  Text,
  ScrollArea,
  Separator,
  DropdownMenu,
  Dialog,
  TextField,
  Button,
} from '@radix-ui/themes';
import { useLocation, Link } from 'react-router-dom';
import {
  CubeIcon,
  ActivityLogIcon,
  GearIcon,
  ChevronDownIcon,
  ListBulletIcon,
  ExitIcon,
  CodeIcon,
  FileTextIcon,
  HamburgerMenuIcon,
  Cross1Icon,
  BellIcon,
  LightningBoltIcon,
  MixerHorizontalIcon,
  EnvelopeClosedIcon,
  IdCardIcon,
} from '@radix-ui/react-icons';
import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { toast } from '../../utils/toast';
import { useCreateTeamMutation } from '../../graphql/mutations/CreateTeam.generated';
import { useGetMyTeamsQuery } from '../../graphql/queries/GetMyTeams.generated';
import LogoutButton from '../LogoutButton';
import './styles.css';

interface NavItem {
  title: string;
  path: string;
  icon: React.ComponentType<any>;
}

interface NavSection {
  title: string;
  items: NavItem[];
  defaultOpen?: boolean;
}

const navSections: NavSection[] = [
  {
    title: 'Indexer',
    defaultOpen: true,
    items: [
      {
        title: 'Blocks',
        path: '/blocks',
        icon: CubeIcon,
      },
      {
        title: 'Transactions',
        path: '/transactions',
        icon: ActivityLogIcon,
      },
      {
        title: 'Contracts',
        path: '/contracts',
        icon: CodeIcon,
      },
      {
        title: 'Events',
        path: '/events',
        icon: FileTextIcon,
      },
    ],
  },
  {
    title: 'Intelligence',
    defaultOpen: true,
    items: [
      {
        title: 'Entities',
        path: '/entities',
        icon: IdCardIcon,
      },
      {
        title: 'Lists',
        path: '/lists',
        icon: ListBulletIcon,
      },
      {
        title: 'Signals',
        path: '/signals',
        icon: MixerHorizontalIcon,
      },
    ],
  },
  {
    title: 'Automation',
    defaultOpen: true,
    items: [
      {
        title: 'Alerts',
        path: '/alerts',
        icon: BellIcon,
      },
      {
        title: 'Workflows',
        path: '/workflows',
        icon: LightningBoltIcon,
      },
    ],
  },
];

const Sidebar = () => {
  const location = useLocation();
  const { user } = useAuth();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>(
    navSections.reduce(
      (acc, section) => ({
        ...acc,
        [section.title]: section.defaultOpen ?? true,
      }),
      {},
    ),
  );
  const [isCreateTeamDialogOpen, setIsCreateTeamDialogOpen] = useState(false);
  const [teamName, setTeamName] = useState('');
  const { data: teamsData, refetch: refetchTeams } = useGetMyTeamsQuery();
  const [createTeam, { loading: isCreating }] = useCreateTeamMutation();

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileOpen]);

  const toggleSection = (sectionTitle: string) => {
    setOpenSections((prev) => ({
      ...prev,
      [sectionTitle]: !prev[sectionTitle],
    }));
  };

  const toggleMobileMenu = () => {
    setIsMobileOpen((prev) => !prev);
  };

  const closeMobileMenu = () => {
    setIsMobileOpen(false);
  };

  const handleCreateTeam = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!teamName.trim()) {
      toast.show({
        title: 'Team name required',
        description: 'Please enter a team name',
        variant: 'destructive',
      });
      return;
    }

    try {
      const result = await createTeam({
        variables: {
          input: {
            name: teamName.trim(),
          },
        },
      });

      if (result.data) {
        toast.show({
          title: 'Team created',
          description: `Successfully created team "${teamName}"`,
        });

        // Refetch teams to update the list
        await refetchTeams();

        setIsCreateTeamDialogOpen(false);
        setTeamName('');
      }
    } catch (error) {
      console.error('Create team error:', error);
      toast.show({
        title: 'Failed to create team',
        description:
          error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      });
    }
  };

  // Get initials from user name or email
  const getInitials = (name: string, email: string) => {
    if (name) {
      return name
        .split(' ')
        .map((word) => word.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return email.charAt(0).toUpperCase();
  };

  return (
    <>
      {/* Mobile menu button */}
      <button
        className="mobile-menu-button"
        onClick={toggleMobileMenu}
        aria-label="Toggle menu"
      >
        {isMobileOpen ? (
          <Cross1Icon width="20" height="20" />
        ) : (
          <HamburgerMenuIcon width="20" height="20" />
        )}
      </button>

      {/* Backdrop overlay for mobile */}
      <div
        className={`sidebar-backdrop ${isMobileOpen ? 'visible' : ''}`}
        onClick={closeMobileMenu}
      />

      {/* Sidebar */}
      <Flex
        direction="column"
        className={`sidebar ${isMobileOpen ? 'mobile-open' : ''}`}
      >
        {/* Header with user info */}
        <Box className="sidebar-header">
          <DropdownMenu.Root>
            <DropdownMenu.Trigger>
              <button className="workspace-selector">
                <Flex align="center" gap="2" style={{ width: '100%' }}>
                  <Box
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      background:
                        'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '13px',
                      color: 'white',
                      fontWeight: '600',
                      flexShrink: 0,
                    }}
                  >
                    {getInitials(user?.name || '', user?.email || '')}
                  </Box>
                  <Flex
                    direction="column"
                    align="start"
                    gap="0"
                    style={{ flex: 1, minWidth: 0 }}
                  >
                    <Text size="2" weight="medium" className="workspace-name">
                      {user?.name || user?.email?.split('@')[0] || 'Personal'}
                    </Text>
                    <Text size="1" className="workspace-email">
                      {user?.email}
                    </Text>
                  </Flex>
                  <ChevronDownIcon
                    style={{ marginLeft: 'auto', flexShrink: 0 }}
                  />
                </Flex>
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content style={{ minWidth: '200px' }}>
              <DropdownMenu.Label>
                <Text size="1" color="gray">
                  Workspaces
                </Text>
              </DropdownMenu.Label>

              {/* Personal Workspace */}
              <DropdownMenu.Item>
                <Flex align="center" gap="2">
                  <Box
                    style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '4px',
                      background:
                        'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '10px',
                      color: 'white',
                    }}
                  >
                    {getInitials(user?.name || '', user?.email || '')}
                  </Box>
                  <Text size="2">
                    {user?.name || user?.email?.split('@')[0] || 'Personal'}
                  </Text>
                </Flex>
              </DropdownMenu.Item>

              {/* Team Workspaces */}
              {teamsData?.myTeams && teamsData.myTeams.length > 0 && (
                <>
                  <DropdownMenu.Separator />
                  <DropdownMenu.Label>
                    <Text size="1" color="gray">
                      Teams
                    </Text>
                  </DropdownMenu.Label>
                  {teamsData.myTeams.map((team) => (
                    <DropdownMenu.Item key={team.id}>
                      <Flex align="center" gap="2">
                        <Box
                          style={{
                            width: '20px',
                            height: '20px',
                            borderRadius: '4px',
                            background:
                              'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '10px',
                            color: 'white',
                          }}
                        >
                          {team.name.charAt(0).toUpperCase()}
                        </Box>
                        <Text size="2">{team.name}</Text>
                      </Flex>
                    </DropdownMenu.Item>
                  ))}
                </>
              )}

              <DropdownMenu.Separator />
              <DropdownMenu.Item
                onSelect={() => setIsCreateTeamDialogOpen(true)}
              >
                <Text size="2">+ Create Team</Text>
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        </Box>

        <Separator size="4" />

        {/* Navigation sections */}
        <ScrollArea className="sidebar-nav" scrollbars="vertical">
          <Box p="3">
            {navSections.map((section, idx) => (
              <Box key={section.title} mb="3">
                {/* Section header */}
                <button
                  className="sidebar-section-header"
                  onClick={() => toggleSection(section.title)}
                >
                  <Text
                    size="1"
                    weight="medium"
                    className="sidebar-section-title"
                  >
                    {section.title}
                  </Text>
                  <ChevronDownIcon
                    className={`sidebar-section-icon ${openSections[section.title] ? 'open' : ''}`}
                  />
                </button>

                {/* Section items */}
                {openSections[section.title] && (
                  <Flex direction="column" gap="1" mt="2">
                    {section.items.map((item) => {
                      const Icon = item.icon;
                      const isActive =
                        location.pathname === item.path ||
                        (item.path !== '/' &&
                          location.pathname.startsWith(item.path));

                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          className={`sidebar-nav-item ${isActive ? 'active' : ''}`}
                        >
                          <Icon className="sidebar-nav-icon" />
                          <Text size="2" className="sidebar-nav-text">
                            {item.title}
                          </Text>
                        </Link>
                      );
                    })}
                  </Flex>
                )}
              </Box>
            ))}
          </Box>
        </ScrollArea>

        {/* Footer with Notifications, Settings and Logout */}
        <Box className="sidebar-footer">
          <Separator size="4" />

          <Box p="3">
            <Link
              to="/notifications"
              className={`sidebar-nav-item ${
                location.pathname.startsWith('/notifications') ? 'active' : ''
              }`}
            >
              <EnvelopeClosedIcon className="sidebar-nav-icon" />
              <Text size="2" className="sidebar-nav-text">
                Notifications
              </Text>
            </Link>

            <Link
              to="/settings"
              className={`sidebar-nav-item ${
                location.pathname.startsWith('/settings') ? 'active' : ''
              }`}
            >
              <GearIcon className="sidebar-nav-icon" />
              <Text size="2" className="sidebar-nav-text">
                Settings
              </Text>
            </Link>

            <LogoutButton className="sidebar-nav-item">
              <ExitIcon className="sidebar-nav-icon" />
              <Text size="2" className="sidebar-nav-text">
                Logout
              </Text>
            </LogoutButton>
          </Box>
        </Box>

        {/* Create Team Dialog */}
        <Dialog.Root
          open={isCreateTeamDialogOpen}
          onOpenChange={setIsCreateTeamDialogOpen}
        >
          <Dialog.Content style={{ maxWidth: '450px' }}>
            <Dialog.Title>Create Team</Dialog.Title>
            <Dialog.Description size="2" mb="4">
              Create a new team to collaborate with others.
            </Dialog.Description>

            <form onSubmit={handleCreateTeam}>
              <Flex direction="column" gap="3">
                <label>
                  <Text as="div" size="2" mb="1" weight="bold">
                    Team Name
                  </Text>
                  <TextField.Root
                    placeholder="Enter team name"
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                    disabled={isCreating}
                  />
                </label>

                <Flex gap="3" mt="4" justify="end">
                  <Dialog.Close>
                    <Button variant="soft" color="gray" disabled={isCreating}>
                      Cancel
                    </Button>
                  </Dialog.Close>
                  <Button type="submit" disabled={isCreating}>
                    {isCreating ? 'Creating...' : 'Create Team'}
                  </Button>
                </Flex>
              </Flex>
            </form>
          </Dialog.Content>
        </Dialog.Root>
      </Flex>
    </>
  );
};

export default Sidebar;
